import * as Popover from '@radix-ui/react-popover'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ENABLED_LANGUAGES } from '@app/i18n'

import { cn } from '@shared/lib/cn'
import { useLocale, type SupportedLocale } from '@shared/lib/locale'

import styles from './styles.module.scss'

import ArrowIcon from '/icons/arrow.svg'
// import EnIcon from '/icons/locale/en.svg'
import KkIcon from '/icons/locale/kk.svg'
import RuIcon from '/icons/locale/ru.svg'

interface SwitchLocaleProps {
  className?: string;
  isMenu?: boolean;
}

type LanguageOption = {
  code: string;
  name: string;
  icon: string;
}

export default function SwitchLocale ({ className, isMenu }: SwitchLocaleProps) {
  const { i18n } = useTranslation()
  const { t: tn } = useTranslation('navigation')
  const { setLocale } = useLocale()
  const { language } = i18n
  const [ isOpen, setIsOpen ] = useState(false)

  const languages = useMemo<LanguageOption[]>(() => {
    const languageMap: Record<string, LanguageOption> = {
      ru: { code: 'ru', name: 'Русский', icon: RuIcon },
      kk: { code: 'kk', name: 'Қазақша', icon: KkIcon }
    }

    return ENABLED_LANGUAGES
      .map((code) => languageMap[code])
      .filter((lang): lang is LanguageOption => Boolean(lang))
  }, [ ENABLED_LANGUAGES ])

  const handleToggle = () => setIsOpen((prev) => !prev)

  useEffect(() => {
    if (languages.length === 1 && language !== languages[0].code) {
      setLocale(languages[0].code as SupportedLocale)
    }
  }, [ setLocale, language, languages ])

  if (languages.length <= 1) {
    return null
  }

  const renderLanguageList = () =>
    languages.map((lang) => (
      <button
        key={lang.code}
        className={cn('reset-button', styles.language, {
          [styles._current]: language == lang.code,
        })}
        type="button"
        onClick={() => {
          setLocale(lang.code as SupportedLocale)
          if (isMenu) {
            setIsOpen(false)
          }
        }}
      >
        <span className={styles['language__icon']}>
          <img
            src={lang.icon}
            alt={lang.name}
            loading="lazy"
            fetchPriority="low"
          />
        </span>
        {lang.name}
      </button>
    ))

  const renderTrigger = (isMenu?: boolean) => (
    <button
      className={cn(styles.trigger, 'reset-button', className, {
        [styles.triggerInline]: isMenu,
        [styles.triggerInlineOpen]: isOpen,
      })}
    >
      {isMenu && (
        <span className={styles.triggerLabel}>{tn('language.label')}</span>
      )}
      {language}
      <img
        className={styles['trigger__icon']}
        src={ArrowIcon}
        alt=""
        loading="lazy"
        fetchPriority="low"
      />
    </button>
  )

  if (isMenu) {
    return (
      <div className={styles.triggerStatic}>
        <div className={styles.triggerBtn} onClick={handleToggle}>
          {renderTrigger(isMenu)}
        </div>
        <div
          className={cn(styles.triggerBox, {
            [styles.triggerBoxVisible]: isOpen,
          })}
        >
          <span className={styles.triggerBoxTitle}>
            {tn('language.select')}
          </span>
          {renderLanguageList()}
        </div>
      </div>
    )
  }

  return (
    <Popover.Root>
      <Popover.Trigger asChild>{renderTrigger()}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className={styles.content} sideOffset={15}>
          {renderLanguageList()}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
